(function (root) {
  'use strict';

  const clean = value => String(value ?? '').normalize('NFC')
    .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '')
    .replace(/[\u2010-\u2015]/g, '-')
    .replace(/[\u2190-\u2199]/g, match => ({ '←': '<-', '→': '->', '↔': '<->' }[match] || '->'))
    .replace(/[₀₁₂₃₄₅₆₇₈₉]/g, match => '0123456789'['₀₁₂₃₄₅₆₇₈₉'.indexOf(match)])
    .replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹]/g, match => '0123456789'['⁰¹²³⁴⁵⁶⁷⁸⁹'.indexOf(match)]);
  const money = (cents, currency) => new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(cents / 100);

  async function buildPdf(model, libraries) {
    const { PDFLib } = libraries;
    const { PDFDocument, rgb, PDFString, StandardFonts } = PDFLib;
    const pdf = await PDFDocument.create();
    // The built-in PDF fonts avoid invalid TrueType embedding in WebKit and
    // remain compatible with Preview, Acrobat and Poppler.
    const regular = await pdf.embedFont(StandardFonts.Helvetica);
    const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
    const glyphs = new Map([[regular, new Set(regular.getCharacterSet())], [bold, new Set(bold.getCharacterSet())]]);
    const ink = rgb(.055, .16, .14), muted = rgb(.31, .37, .36), blue = rgb(.09, .25, .69), rule = rgb(.82, .86, .84);
    const width = 595.28, height = 841.89, margin = 44, usable = width - 2 * margin, bottom = 65;
    let page, y;
    const date = new Intl.DateTimeFormat('de-DE').format(new Date(model.createdAt));
    const title = model.type === 'list' ? 'Produktmerkliste' : 'Produktübersicht';
    pdf.setTitle(`${title} - ${model.type === 'list' ? 'Bildungsmarktplatz.net' : model.items[0].title}`);
    pdf.setAuthor('Bildungsmarktplatz.net');
    pdf.setCreator('Bildungsmarktplatz.net PDF-Export');
    pdf.setLanguage('de-DE');

    function printable(text, font = regular) {
      const supported = glyphs.get(font);
      return Array.from(clean(text)).map(c => c === '\n' || supported.has(c.codePointAt(0)) ? c : '?').join('');
    }
    function lines(text, max, size, font = regular) {
      const out = [];
      for (const paragraph of printable(text, font).split(/\n/)) {
        if (!paragraph.trim()) { out.push(''); continue; }
        let line = '';
        for (const word of paragraph.trim().split(/\s+/)) {
          const candidate = line ? `${line} ${word}` : word;
          if (font.widthOfTextAtSize(candidate, size) <= max) { line = candidate; continue; }
          if (line) { out.push(line); line = ''; }
          for (const letter of word) {
            if (line && font.widthOfTextAtSize(line + letter, size) > max) { out.push(line); line = ''; }
            line += letter;
          }
        }
        if (line) out.push(line);
      }
      return out;
    }
    function draw(text, x, baseline, size = 10, font = regular, color = ink) {
      const value = printable(text, font);
      if (!value) return;
      page.drawText(value, { x, y: baseline, size, font, color });
    }
    function newPage() {
      page = pdf.addPage([width, height]);
      page.drawRectangle({ x: margin, y: height - 47, width: 5, height: 16, color: blue });
      draw('Bildungsmarktplatz.net', margin + 14, height - 43, 14, bold);
      draw(`${title} · Stand ${date}`, margin, height - 66, 8, regular, muted);
      page.drawLine({ start: { x: margin, y: height - 80 }, end: { x: width - margin, y: height - 80 }, thickness: .65, color: rule });
      y = height - 103;
    }
    function ensure(space) { if (y - space < bottom) newPage(); }
    function paragraph(text, options = {}) {
      const size = options.size || 10, leading = options.leading || 14.5;
      for (const block of clean(text).split('\n')) {
        const wrapped = lines(block, options.width || usable, size, options.bold ? bold : regular);
        ensure(Math.min(3, wrapped.length) * leading);
        for (const [index, line] of wrapped.entries()) {
          ensure(wrapped.length - index === 2 ? leading * 2 : leading);
          if (line) draw(line, options.x || margin, y, size, options.bold ? bold : regular, options.color || ink);
          y -= line ? leading : leading * .55;
        }
      }
      y -= options.gap ?? 7;
    }
    function heading(text) {
      ensure(49);
      paragraph(text, { size: 12, leading: 16, bold: true, gap: 4 });
    }
    function link(label, url) {
      if (!/^https?:\/\//i.test(url || '')) return;
      for (const line of lines(label, usable, 9, regular)) {
        ensure(14);
        draw(line, margin, y, 9, regular, blue);
        const annotation = pdf.context.register(pdf.context.obj({
          Type: 'Annot', Subtype: 'Link', Rect: [margin, y - 2, margin + regular.widthOfTextAtSize(line, 9), y + 10],
          Border: [0, 0, 0], A: { Type: 'Action', S: 'URI', URI: PDFString.of(url) }
        }));
        page.node.addAnnot(annotation);
        y -= 14;
      }
    }
    async function image(item, box) {
      if (!item.imageBytes) return false;
      try {
        const embedded = item.imageType === 'png' ? await pdf.embedPng(item.imageBytes) : await pdf.embedJpg(item.imageBytes);
        const scale = Math.min(box.width / embedded.width, box.height / embedded.height);
        const w = embedded.width * scale, h = embedded.height * scale;
        page.drawImage(embedded, { x: box.x + (box.width - w) / 2, y: box.y + (box.height - h) / 2, width: w, height: h });
        return true;
      } catch (_) { return false; }
    }

    newPage();
    if (model.type === 'product') {
      const item = model.items[0];
      paragraph(item.title, { size: 23, leading: 28, bold: true, gap: 3 });
      if (item.variant) paragraph(item.variant, { size: 11, color: muted });
      ensure(160);
      const top = y;
      const hasImage = await image(item, { x: margin, y: top - 137, width: 156, height: 133 });
      const x = hasImage ? margin + 178 : margin;
      const column = hasImage ? usable - 178 : usable;
      paragraph(item.priceText, { x, width: column, size: 21, leading: 26, bold: true, gap: 2 });
      paragraph(model.priceNote, { x, width: column, size: 8, leading: 11 });
      if (item.sku) paragraph(`Artikelnummer: ${item.sku}`, { x, width: column, size: 9, leading: 13, gap: 3 });
      paragraph(`Gewählte Menge: ${item.quantity}`, { x, width: column, size: 9, leading: 13, gap: 3 });
      paragraph(item.available ? 'Im Shop bestellbar' : 'Derzeit nicht bestellbar', { x, width: column, size: 9, leading: 13 });
      y = Math.min(y, top - (hasImage ? 153 : 0));
      if (item.imageWarning) paragraph(item.imageWarning, { size: 8, color: muted });
      heading('Produktbeschreibung');
      paragraph(item.description || 'Weitere Angaben auf der Produktseite.');
      for (const section of model.sections || []) {
        if (!section.text) continue;
        heading(section.title);
        paragraph(section.text);
      }
      heading('Produkt und weiterführende Dokumente');
      link('Produktseite öffnen', item.url);
      paragraph(item.url, { size: 8, leading: 11, color: muted, gap: 5 });
      for (const doc of model.links || []) link(doc.label, doc.url);
    } else {
      paragraph('Produktmerkliste', { size: 26, leading: 31, bold: true, gap: 4 });
      paragraph(`${model.items.length} Positionen · ${model.items.reduce((sum, item) => sum + item.quantity, 0)} Stück`, { size: 10, color: muted });
      paragraph('Zur Abstimmung und Beschaffung. Diese Liste ist keine Bestellung, kein verbindliches Angebot und keine Rechnung.', { size: 9, color: muted });
      const columns = () => {
        ensure(47);
        page.drawRectangle({ x: margin, y: y - 9, width: usable, height: 24, color: rgb(.94, .96, .95) });
        draw('PRODUKT / AUSFÜHRUNG', margin + 7, y, 8, bold);
        draw('MENGE', 365, y, 8, bold);
        draw('EINZEL', 423, y, 8, bold);
        draw('SUMME', 496, y, 8, bold);
        y -= 33;
      };
      columns();
      let total = 0, complete = true;
      for (const [index, item] of model.items.entries()) {
        const details = [item.variant, item.sku ? `Art.-Nr. ${item.sku}` : '', item.available === false ? 'Derzeit nicht bestellbar' : '', item.warning || ''].filter(Boolean);
        const titleLines = lines(`${index + 1}. ${item.title}`, 244, 10, bold);
        const detailLines = lines(details.join('\n'), 244, 8);
        const rowHeight = Math.max(66, titleLines.length * 14 + detailLines.length * 11 + 25);
        if (y - rowHeight < bottom) { newPage(); columns(); }
        const start = y;
        for (const line of titleLines) { draw(line, margin + 7, y, 10, bold); y -= 14; }
        for (const line of detailLines) { draw(line, margin + 7, y, 8, regular, muted); y -= 11; }
        const rowPrice = Number.isSafeInteger(item.priceCents) ? money(item.priceCents, model.currency) : 'offen';
        const sum = Number.isSafeInteger(item.priceCents) ? money(item.priceCents * item.quantity, model.currency) : 'offen';
        draw(String(item.quantity), 380, start, 10);
        draw(rowPrice, 462 - regular.widthOfTextAtSize(rowPrice, 9), start, 9);
        draw(sum, width - margin - 7 - bold.widthOfTextAtSize(sum, 9), start, 9, bold);
        link(`Produkt ${index + 1} öffnen`, item.url);
        if (Number.isSafeInteger(item.priceCents)) total += item.priceCents * item.quantity;
        else complete = false;
        y = Math.min(y - 8, start - rowHeight);
        page.drawLine({ start: { x: margin, y }, end: { x: width - margin, y }, thickness: .5, color: rule });
        y -= 18;
      }
      ensure(90);
      paragraph(`${complete ? 'Warensumme' : 'Zwischensumme bekannter Preise'}: ${money(total, model.currency)}`, { size: 16, leading: 21, bold: true });
      paragraph(model.priceNote, { size: 9, color: muted });
      if (!complete) paragraph('Mindestens eine Position konnte nicht vollständig aufgelöst werden. Die angezeigte Zwischensumme ist deshalb kein Gesamtpreis.', { size: 9, color: muted });
    }
    y -= 6;
    paragraph('Preise und Verfügbarkeit: Momentaufnahme zum Exportdatum. Maßgeblich sind die Angaben im Shop bei der Bestellung. Versandkosten sind nicht Bestandteil dieser Übersicht.', { size: 8, leading: 11, color: muted });
    pdf.getPages().forEach((p, index, all) => {
      page = p;
      page.drawLine({ start: { x: margin, y: 48 }, end: { x: width - margin, y: 48 }, thickness: .65, color: rule });
      draw('Bildungsmarktplatz.net · AVR · Produkt- und Beschaffungsübersicht', margin, 33, 7, regular, muted);
      const number = `${index + 1} / ${all.length}`;
      draw(number, width - margin - regular.widthOfTextAtSize(number, 8), 33, 8, regular, muted);
    });
    return pdf.save();
  }

  root.AdbPrintPDF = { buildPdf, clean };
  if (typeof document === 'undefined') return;
  const script = document.querySelector('script[data-adb-pdf-lib]');
  let librariesPromise;
  function loadScript(src) {
    return new Promise((resolve, reject) => {
      const el = document.createElement('script');
      const fail = () => { clearTimeout(timer); el.remove(); reject(new Error('PDF-Bibliothek konnte nicht geladen werden. Bitte erneut versuchen.')); };
      const timer = setTimeout(fail, 20000);
      el.src = src; el.onload = () => { clearTimeout(timer); resolve(); }; el.onerror = fail;
      document.head.append(el);
    });
  }
  async function bounded(promise) {
    let timer;
    try {
      return await Promise.race([promise, new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Die PDF-Erstellung dauert zu lange. Bitte erneut versuchen.')), 45000); })]);
    } finally { clearTimeout(timer); }
  }
  async function bytes(url) {
    const response = await fetch(url, { signal: AbortSignal.timeout(15000) });
    if (!response.ok) throw new Error('PDF-Daten konnten nicht geladen werden. Bitte erneut versuchen.');
    return new Uint8Array(await response.arrayBuffer());
  }
  function libraries() {
    if (!librariesPromise) librariesPromise = (root.PDFLib ? Promise.resolve() : loadScript(script.dataset.adbPdfLib))
      .then(() => ({ PDFLib: root.PDFLib }))
      .catch(error => { librariesPromise = null; throw error; });
    return librariesPromise;
  }
  const plain = html => {
    const node = document.createElement('div');
    node.innerHTML = html || '';
    node.querySelectorAll('script,style,iframe,form,[aria-hidden="true"]').forEach(el => el.remove());
    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) walker.currentNode.textContent = walker.currentNode.textContent.replace(/[\t\r\n]+/g, ' ');
    node.querySelectorAll('br').forEach(el => el.replaceWith('\n'));
    node.querySelectorAll('td,th').forEach(el => el.append(' | '));
    node.querySelectorAll('p,div,li,tr,dt,dd,h1,h2,h3,h4,h5').forEach(el => el.append('\n'));
    return (node.textContent || '').replace(/[ \t]+/g, ' ').replace(/\n\s*\n\s*\n/g, '\n\n').trim();
  };
  async function productItem(saved, currency, required) {
    const url = new URL(saved.url, location.origin);
    if (url.origin !== location.origin || !/^\/products\/[a-z0-9-]+\/?$/i.test(url.pathname)) throw new Error('Ungültiger Produktlink.');
    try {
      const response = await fetch(`${url.pathname.replace(/\/$/, '')}.js`, { signal: AbortSignal.timeout(15000), headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Produktdaten sind momentan nicht abrufbar.');
      const product = await response.json();
      const id = String(saved.variantId || url.searchParams.get('variant') || '');
      if (!id && product.variants.length > 1 && !required) throw new Error('Ausführung bitte auf der Produktseite festlegen; Preis noch offen.');
      const variant = id ? product.variants.find(v => String(v.id) === id) : product.variants[0];
      if (!variant) throw new Error('Gespeicherte Variante ist nicht mehr verfügbar.');
      url.search = ''; url.hash = ''; url.searchParams.set('variant', variant.id);
      const featured = variant.featured_image?.src || product.featured_image;
      return { title: product.title, variant: variant.title === 'Default Title' ? '' : variant.title, sku: variant.sku, quantity: saved.quantity || 1, priceCents: variant.price, priceText: money(variant.price, currency), available: variant.available, description: plain(product.description), url: url.href, imageUrl: featured };
    } catch (error) {
      if (required) throw error;
      return { title: saved.title, quantity: saved.quantity || 1, url: url.href, warning: error.message, priceCents: null };
    }
  }
  async function productImage(item) {
    if (!item.imageUrl) return;
    try {
      const url = new URL(item.imageUrl, location.origin);
      if (url.protocol !== 'https:' || !(url.origin === location.origin || url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.myshopify.com'))) throw new Error('Bildquelle');
      url.searchParams.set('width', '1000');
      const response = await fetch(url.href, { signal: AbortSignal.timeout(12000) });
      if (!response.ok) throw new Error('Bild nicht geladen');
      const blob = await response.blob();
      if (/image\/(png|jpeg)/.test(blob.type)) { item.imageType = blob.type.includes('png') ? 'png' : 'jpg'; item.imageBytes = new Uint8Array(await blob.arrayBuffer()); return; }
      const bitmap = await createImageBitmap(blob);
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width; canvas.height = bitmap.height;
      const context = canvas.getContext('2d');
      context.fillStyle = '#fff'; context.fillRect(0, 0, canvas.width, canvas.height); context.drawImage(bitmap, 0, 0); bitmap.close();
      const jpeg = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', .95));
      item.imageType = 'jpg'; item.imageBytes = new Uint8Array(await jpeg.arrayBuffer());
    } catch (_) { item.imageWarning = 'Das Produktbild konnte für diesen Export nicht geladen werden. Es ist auf der verlinkten Produktseite verfügbar.'; }
  }
  function sections() {
    return Array.from(document.querySelectorAll('.adb-product-fit, .adb-product-learning-trail, .adb-product-selection, .adb-product-procurement, .adb-product-safety')).map(node => {
      const clone = node.cloneNode(true);
      const title = clone.querySelector('h2')?.textContent.trim() || node.getAttribute('aria-label') || 'Produktangaben';
      clone.querySelectorAll('.adb-product-safety__documents').forEach(el => el.parentElement.remove());
      clone.querySelectorAll('h2,.adb-eyebrow,svg,img,button,.adb-product-safety__documents').forEach(el => el.remove());
      return { title, text: plain(clone.innerHTML) };
    });
  }
  async function exportPdf(options) {
    const button = options.button;
    if (button.disabled) return;
    const scope = button.closest('dialog, [data-adb-product-tools]') || button.parentElement;
    let status = scope.querySelector('[data-adb-pdf-status]');
    if (!status) { status = document.createElement('p'); status.dataset.adbPdfStatus = ''; status.className = 'adb-pdf-status'; status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite'); scope.append(status); }
    if (status.dataset.blobUrl) URL.revokeObjectURL(status.dataset.blobUrl);
    status.textContent = 'PDF wird erstellt …'; button.disabled = true; button.setAttribute('aria-busy', 'true');
    try {
      const currency = script.dataset.adbPdfCurrency || 'EUR';
      const model = { type: options.type, currency, createdAt: new Date().toISOString(), priceNote: script.dataset.adbPdfTax === 'true' ? 'Preise inklusive Steuern, zuzüglich Versandkosten.' : 'Steuern und Versandkosten werden im Checkout berechnet.' };
      if (options.type === 'list') {
        if (!options.items?.length) throw new Error('Die Merkliste ist noch leer.');
        model.items = [];
        for (let offset = 0; offset < options.items.length; offset += 4) model.items.push(...await Promise.all(options.items.slice(offset, offset + 4).map(item => productItem(item, currency, false))));
      } else {
        const form = document.querySelector('product-form-component form[action*="/cart/add"], form[action*="/cart/add"]');
        const variantId = form?.querySelector('[name="id"]')?.value || new URL(location.href).searchParams.get('variant');
        const quantity = Math.max(1, Number.parseInt(form?.querySelector('[name="quantity"]')?.value || '1', 10) || 1);
        model.items = [await productItem({ url: location.href, variantId, quantity }, currency, true)];
        await productImage(model.items[0]);
        model.sections = sections();
        model.links = Array.from(document.querySelectorAll('.adb-product-safety a[href], .product-information .rte a[href]')).filter(a => /^https?:$/.test(new URL(a.href).protocol)).map(a => ({ label: plain(a.innerHTML).replace(/\s+/g, ' ') || 'Produktdokument', url: a.href }));
      }
      status.textContent = 'PDF-Schriften werden geladen …';
      const fonts = await libraries();
      status.textContent = 'PDF-Seiten werden gesetzt …';
      const data = await bounded(buildPdf(model, fonts));
      if (!status.isConnected) return;
      const blobUrl = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      status.dataset.blobUrl = blobUrl;
      const download = document.createElement('a');
      download.href = blobUrl;
      const filename = options.type === 'list' ? 'Produktmerkliste' : model.items[0].title.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 90);
      download.download = `Bildungsmarktplatz-${filename}-${model.createdAt.slice(0, 10)}.pdf`;
      download.textContent = 'PDF herunterladen';
      const preview = document.createElement('a');
      preview.href = blobUrl; preview.target = '_blank'; preview.rel = 'noopener'; preview.textContent = 'PDF ansehen';
      status.replaceChildren(document.createTextNode('PDF ist fertig. '), download, document.createTextNode(' · '), preview);
      download.click();
    } catch (error) {
      status.textContent = `PDF konnte nicht erstellt werden: ${error.message} Die Liste und der Warenkorb bleiben unverändert.`;
    } finally { button.disabled = false; button.removeAttribute('aria-busy'); }
  }
  root.AdbPrint = { exportPdf };
})(typeof window === 'undefined' ? globalThis : window);
